import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {useSelector} from 'react-redux';
import {getCards, getGroupById} from 'stores/selectors/cardStateSelector';
import {getValidCards} from 'lib/cardAndTaskHelper';

import CardName from './CardName';

import './CardGroup.scss';
import {mergeRefs} from '../../../../lib/refHelper';
import TaskListCheckbox from '../Components/TaskListCheckbox';

const CardGroup = React.forwardRef(
  (
    {
      drop,
      drag,
      disableDrag,
      ID,
      children,
      isExpanded,
      selected,
      isIgnored,
      name,
      onClickExpand,
      onMouseDownHandler,
      onShowMenuContext,
      onCheckGroup,
      onCardGroupNameChange,
      maxCharacters,
      runtimeMode,
      isViewMode,
      disableMenuContext,
    },
    ref,
  ) => {
    const onCardGroupNameChangeHandler = (value) => {
      onCardGroupNameChange(ID, name, value);
    };
    const cards = useSelector(getCards);
    const group = useSelector(getGroupById(ID));

    const groupRequiredTips = useMemo(
      () =>
        getValidCards(Object.values(cards).filter((card) => group.cards.includes(card.ID))).reduce(
          (acc, card) => acc + (card.requiredTips || 0),
          0,
        ),
      [cards, group],
    );

    return (
      <div className={`card-group ${selected ? 'selected' : ''}`}>
        <div
          ref={!disableDrag ? mergeRefs(drop, drag, ref) : mergeRefs(drop, ref)}
          className={`card-group__header ${selected ? 'selected' : ''}`}
          onMouseDown={(e) => {
            if (e.button === 0) {
              // only listen to left button mouse click
              onMouseDownHandler(e, ID, isExpanded);
            }
          }}
          onContextMenu={(e) => {
            if (!disableMenuContext) {
              onShowMenuContext(e);
            }
          }}
          role="presentation"
        >
          <div className="card-group__header__left">
            <div onMouseDown={(e) => e.stopPropagation()} aria-hidden="true">
              <span
                className={`icon-triangle-${isExpanded ? 'down' : 'right'} expand`}
                onMouseDown={() => onClickExpand(ID, isExpanded)}
                role="presentation"
              />
            </div>
            <span className="icon-folder" />
            <CardName
              className=""
              name={name}
              onCardNameChange={onCardGroupNameChangeHandler}
              tooltip=""
              maxCharacters={maxCharacters}
              runtimeMode={runtimeMode}
              isViewMode={isViewMode}
            />
          </div>
          <div className="card-group__header__right">
            <span className="tipsCount">{groupRequiredTips} tips</span>
            {!runtimeMode && !isViewMode && (
              <TaskListCheckbox checked={isIgnored} onChange={(e) => onCheckGroup(e.target.checked)} />
            )}
          </div>
        </div>
        <div className={`card-group__content ${!isExpanded ? 'hidden' : ''}`}>{children}</div>
      </div>
    );
  },
);

CardGroup.propTypes = {
  drop: PropTypes.any,
  drag: PropTypes.func,
  disableDrag: PropTypes.bool,
  children: PropTypes.any.isRequired,
  ID: PropTypes.number.isRequired,
  selected: PropTypes.bool,
  isIgnored: PropTypes.bool,
  isExpanded: PropTypes.bool,
  runtimeMode: PropTypes.bool,
  name: PropTypes.string,
  maxCharacters: PropTypes.number,
  onCardGroupNameChange: PropTypes.func,
  onClickExpand: PropTypes.func,
  onMouseDownHandler: PropTypes.func,
  onShowMenuContext: PropTypes.func,
  onCheckGroup: PropTypes.func,
  isViewMode: PropTypes.bool.isRequired,
  disableMenuContext: PropTypes.bool,
};

CardGroup.defaultProps = {
  drop: null,
  drag: null,
  disableDrag: true,
  selected: false,
  isExpanded: false,
  isIgnored: false,
  runtimeMode: false,
  name: '',
  maxCharacters: 25,
  disableMenuContext: false,
  onCardGroupNameChange: () => {},
  onClickExpand: () => {},
  onMouseDownHandler: () => {},
  onShowMenuContext: () => {},
  onCheckGroup: () => {},
};

export default CardGroup;
